import { format, addDays, parseISO } from "date-fns";
import {
  Box,
  TextField,
  IconButton,
  Button,
  type SxProps,
  type Theme,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { getBusinessToday } from "@/utils/businessTime";

const DATE_FIELD_SX = { minWidth: 150 };

function getTodayStr(): string {
  return getBusinessToday();
}

export interface DateRangeFilterProps {
  /** Date de début (YYYY-MM-DD) */
  dateFrom: string;
  /** Date de fin (YYYY-MM-DD) */
  dateTo: string;
  onChangeFrom: (value: string) => void;
  onChangeTo: (value: string) => void;
  /** Afficher les flèches de navigation jour par jour */
  showNavArrows?: boolean;
  /** Afficher le bouton "Aujourd'hui" */
  showTodayButton?: boolean;
  /** Afficher le bouton "Effacer" (quand les dates sont optionnelles) */
  showClearButton?: boolean;
  /** Labels personnalisés */
  labelFrom?: string;
  labelTo?: string;
  /** Désactivé (ex: pendant chargement) */
  disabled?: boolean;
  /** Styles additionnels */
  sx?: SxProps<Theme>;
  /** Taille des champs */
  size?: "small" | "medium";
}

/**
 * Filtre de période harmonisé (Du / Au) pour les écrans de l'application.
 * Même style et interactions sur OrdersPage, EstimationsPage, etc.
 */
export function DateRangeFilter({
  dateFrom,
  dateTo,
  onChangeFrom,
  onChangeTo,
  showNavArrows = true,
  showTodayButton = true,
  showClearButton = false,
  labelFrom = "Du",
  labelTo = "Au",
  disabled = false,
  sx,
  size = "small",
}: DateRangeFilterProps): JSX.Element {
  const baseFrom = dateFrom || getTodayStr();
  const baseTo = dateTo || getTodayStr();

  const handlePrevDay = () => {
    onChangeFrom(format(addDays(parseISO(baseFrom), -1), "yyyy-MM-dd"));
    onChangeTo(format(addDays(parseISO(baseTo), -1), "yyyy-MM-dd"));
  };

  const handleNextDay = () => {
    onChangeFrom(format(addDays(parseISO(baseFrom), 1), "yyyy-MM-dd"));
    onChangeTo(format(addDays(parseISO(baseTo), 1), "yyyy-MM-dd"));
  };

  const handleToday = () => {
    const today = getTodayStr();
    onChangeFrom(today);
    onChangeTo(today);
  };

  const handleClear = () => {
    onChangeFrom("");
    onChangeTo("");
  };

  const fieldSx = { ...DATE_FIELD_SX, ...(size === "small" ? {} : {}) };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {showNavArrows && (
          <IconButton
            size="small"
            onClick={handlePrevDay}
            aria-label={`${labelFrom} précédent`}
            disabled={disabled}
          >
            <ChevronLeft />
          </IconButton>
        )}
        <TextField
          label={labelFrom}
          type="date"
          size={size}
          value={dateFrom}
          onChange={(e) => onChangeFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
          disabled={disabled}
          inputProps={{ "aria-label": `Date ${labelFrom.toLowerCase()}` }}
        />
        {showNavArrows && (
          <IconButton
            size="small"
            onClick={handleNextDay}
            aria-label={`${labelFrom} suivant`}
            disabled={disabled}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {showNavArrows && (
          <IconButton
            size="small"
            onClick={() => onChangeTo(format(addDays(parseISO(baseTo), -1), "yyyy-MM-dd"))}
            aria-label={`${labelTo} précédent`}
            disabled={disabled}
          >
            <ChevronLeft />
          </IconButton>
        )}
        <TextField
          label={labelTo}
          type="date"
          size={size}
          value={dateTo}
          onChange={(e) => onChangeTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
          disabled={disabled}
          inputProps={{ "aria-label": `Date ${labelTo.toLowerCase()}` }}
        />
        {showNavArrows && (
          <IconButton
            size="small"
            onClick={() => onChangeTo(format(addDays(parseISO(baseTo), 1), "yyyy-MM-dd"))}
            aria-label={`${labelTo} suivant`}
            disabled={disabled || !dateTo}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>

      {showTodayButton && (
        <Button
          variant="outlined"
          size={size}
          onClick={handleToday}
          disabled={disabled}
          sx={{ textTransform: "none" }}
        >
          Aujourd'hui
        </Button>
      )}

      {showClearButton && (dateFrom || dateTo) && (
        <Button variant="text" size={size} onClick={handleClear} disabled={disabled}>
          Effacer les dates
        </Button>
      )}
    </Box>
  );
}

export interface DateFilterProps {
  /** Date (YYYY-MM-DD) */
  value: string;
  onChange: (value: string) => void;
  /** Label du champ */
  label?: string;
  /** Afficher les flèches de navigation */
  showNavArrows?: boolean;
  /** Afficher le bouton "Aujourd'hui" */
  showTodayButton?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  size?: "small" | "medium";
}

/**
 * Filtre date unique harmonisé (ex: livraisons).
 * Même style que DateRangeFilter.
 */
export function DateFilter({
  value,
  onChange,
  label = "Date",
  showNavArrows = true,
  showTodayButton = true,
  disabled = false,
  sx,
  size = "small",
}: DateFilterProps): JSX.Element {
  const handlePrev = () => value && onChange(format(addDays(parseISO(value), -1), "yyyy-MM-dd"));
  const handleNext = () => value && onChange(format(addDays(parseISO(value), 1), "yyyy-MM-dd"));
  const handleToday = () => onChange(getTodayStr());

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ...sx }}>
      {showNavArrows && (
        <IconButton size="small" onClick={handlePrev} aria-label="Jour précédent" disabled={disabled || !value}>
          <ChevronLeft />
        </IconButton>
      )}
      <TextField
        label={label}
        type="date"
        size={size}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 150 }}
        disabled={disabled}
        inputProps={{ "aria-label": `Date ${label.toLowerCase()}` }}
      />
      {showNavArrows && (
        <IconButton size="small" onClick={handleNext} aria-label="Jour suivant" disabled={disabled || !value}>
          <ChevronRight />
        </IconButton>
      )}
      {showTodayButton && (
        <Button variant="outlined" size={size} onClick={handleToday} disabled={disabled} sx={{ ml: 0.5, textTransform: "none" }}>
          Aujourd'hui
        </Button>
      )}
    </Box>
  );
}
